/**
 * Retail V1 identity pipeline — Phase 2 §2.3 (PG-020).
 * Formalizes guard chain order and recovery redirect modes without duplicating React guards.
 */
export type CustomerEntitlementGuardMode = 'inline-unavailable' | 'redirect-unavailable';

export const IDENTITY_GUARD_CHAIN = [
  'route-match',
  'entitlement-route-guard',
  'require-customer-auth',
  'account-session-validator',
  'contact-verification-gate',
  'screen-init',
] as const;

export type IdentityPipelineStage = (typeof IDENTITY_GUARD_CHAIN)[number];

export type IdentityPipelineRecoveryMode =
  | 'redirect-unavailable'
  | 'recovery-access-denied'
  | 'recovery-session-expired';

/** Wave 3/4 — both_enforce interstitial (platform chrome only). */
export const COMPLETE_VERIFICATION_PATH = '/account/complete-verification';
/** Wave 3/4 — OIDC optional add-phone (platform chrome only; never `/{tenant}/account/add-phone`). */
export const ADD_PHONE_PATH = '/account/add-phone';

/**
 * Paths allowed while `mustCompleteVerification` is true.
 * Logout is always allowed via session-end (no dedicated path).
 */
export function isContactVerificationAllowlistedPath(pathname: string): boolean {
  const path =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
  return (
    path === COMPLETE_VERIFICATION_PATH ||
    path.startsWith(`${COMPLETE_VERIFICATION_PATH}/`) ||
    path === ADD_PHONE_PATH ||
    path.startsWith(`${ADD_PHONE_PATH}/`)
  );
}

/**
 * Named account hub trees denied until both_enforce gate clears (plan Wave 3).
 * Used for documentation / tests; runtime gate is allowlist-only.
 */
export function isContactVerificationDeniedHubPath(pathname: string): boolean {
  const path =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
  if (isContactVerificationAllowlistedPath(path)) {
    return false;
  }
  if (path === '/account' || path.startsWith('/account/')) {
    return true;
  }
  if (
    path === '/shop' ||
    path.startsWith('/shop/') ||
    path === '/checkout' ||
    path.startsWith('/checkout/')
  ) {
    return true;
  }
  // Tenant-prefixed shop/checkout (retail mounts under `/:tenantCode`).
  if (/^\/[^/]+\/(?:shop|checkout)(?:\/|$)/.test(path)) {
    return true;
  }
  // Banned OIDC land target — never leave the user on tenant-prefixed add-phone.
  if (/^\/[^/]+\/account\/add-phone(?:\/|$)/.test(path)) {
    return true;
  }
  return false;
}

export interface IdentityPipelineInput {
  readonly routeMatched: boolean;
  readonly entitlementsLoading: boolean;
  readonly entitlementsGranted: boolean;
  readonly entitlementMode: CustomerEntitlementGuardMode;
  readonly sessionHydrated: boolean;
  readonly sessionPresent: boolean;
  readonly accountMeReady: boolean;
  readonly accountMeValid: boolean;
  /**
   * G1 — transient `/me` (503 ME_TRANSIENT / keep). Validator shows ServerUnavailableScreen;
   * must not map to recovery-session-expired.
   */
  readonly accountMeTransient?: boolean;
  /**
   * G1 — OIDC land in progress: `?oidcLand=1` **or** Wave 4.6 `oidcPhoneGate=1`+token.
   * Soft-guest (hydrated + null session) must not bounce to sign-in while land is pending.
   */
  readonly oidcLandingPending?: boolean;
  /**
   * G7 — aggregate land wall-clock budget exceeded; account auth may proceed.
   */
  readonly oidcLandBudgetExhausted?: boolean;
  /**
   * Wave 3 — `/me` boolean for native `both_enforce` with still-required unverified channel.
   * When true, only allowlisted paths may `allowScreenInit`.
   */
  readonly mustCompleteVerification?: boolean;
  /** Current location pathname (platform or tenant-prefixed). */
  readonly pathname?: string;
}

export interface IdentityPipelineDecision {
  readonly stage: IdentityPipelineStage;
  readonly allowScreenInit: boolean;
  readonly recoveryMode?: IdentityPipelineRecoveryMode;
  readonly redirectToSignIn?: boolean;
  /** Wave 3/4 — bounce to Dokončit ověření interstitial. */
  readonly redirectToCompleteVerification?: boolean;
}

export function evaluateIdentityPipeline(input: IdentityPipelineInput): IdentityPipelineDecision {
  if (!input.routeMatched) {
    return { stage: 'route-match', allowScreenInit: false };
  }

  if (input.entitlementsLoading) {
    return { stage: 'entitlement-route-guard', allowScreenInit: false };
  }

  if (!input.entitlementsGranted) {
    if (input.entitlementMode === 'redirect-unavailable') {
      return {
        stage: 'entitlement-route-guard',
        allowScreenInit: false,
        recoveryMode: 'redirect-unavailable',
      };
    }
    return {
      stage: 'entitlement-route-guard',
      allowScreenInit: false,
      recoveryMode: 'recovery-access-denied',
    };
  }

  if (!input.sessionHydrated) {
    return { stage: 'require-customer-auth', allowScreenInit: false };
  }

  if (!input.sessionPresent) {
    // G1: token + ?oidcLand=1 → hold at require-customer-auth; do not redirectToSignIn.
    // G7: aggregate budget exhausted — release hold (redirect or guest screen).
    if (
      input.oidcLandingPending === true &&
      input.oidcLandBudgetExhausted !== true
    ) {
      return {
        stage: 'require-customer-auth',
        allowScreenInit: false,
      };
    }
    return {
      stage: 'require-customer-auth',
      allowScreenInit: false,
      redirectToSignIn: true,
    };
  }

  if (input.accountMeTransient === true) {
    return { stage: 'account-session-validator', allowScreenInit: false };
  }

  if (!input.accountMeReady) {
    return { stage: 'account-session-validator', allowScreenInit: false };
  }

  if (!input.accountMeValid) {
    return {
      stage: 'account-session-validator',
      allowScreenInit: false,
      recoveryMode: 'recovery-session-expired',
    };
  }

  if (input.mustCompleteVerification === true) {
    const path = input.pathname ?? '';
    if (!isContactVerificationAllowlistedPath(path)) {
      return {
        stage: 'contact-verification-gate',
        allowScreenInit: false,
        redirectToCompleteVerification: true,
      };
    }
  }

  return { stage: 'screen-init', allowScreenInit: true };
}

export function mapRecoveryModeToPath(
  tenantCode: string,
  mode: IdentityPipelineRecoveryMode,
): string {
  const base = `/${encodeURIComponent(tenantCode)}`;
  switch (mode) {
    case 'redirect-unavailable':
      return `${base}/unavailable`;
    case 'recovery-access-denied':
      return `${base}/recovery/access-denied`;
    case 'recovery-session-expired':
      return `${base}/recovery/session-expired`;
  }
}
